import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Users, Briefcase, ArrowRight, Loader2 } from "lucide-react";
import { SectionHeading } from "./Reveal";
import { reservationApi } from "../../lib/api";

const CAT_BADGE = {
  sedan: "Sedan",
  suv: "SUV",
  van: "Van",
  elite: "Elite",
};

// Deterministic shuffle with de-clustering by visible badge/category.
const mix = (arr) => {
  const seed = (s) => {
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
    return h;
  };
  const seeded = arr
    .map((v) => ({ v, key: (seed(String(v.id)) ^ 0x9e3779b9) >>> 0 }))
    .sort((a, b) => a.key - b.key)
    .map((x) => x.v);
  const buckets = new Map();
  seeded.forEach((v) => {
    const k = v.klass || v.category_key || v.category || "other";
    if (!buckets.has(k)) buckets.set(k, []);
    buckets.get(k).push(v);
  });
  const out = [];
  let prev = null;
  while (out.length < seeded.length) {
    const entries = Array.from(buckets.entries())
      .filter(([, list]) => list.length > 0)
      .sort((a, b) => b[1].length - a[1].length);
    if (entries.length === 0) break;
    let pick = entries.find(([k]) => k !== prev) || entries[0];
    const [cat, list] = pick;
    out.push(list.shift());
    prev = cat;
  }
  return out;
};

// A vehicle is renderable only when it has a working image path AND a real
// name (i.e. not a placeholder/test record). Prevents blank/beige cards.
const isRenderable = (v) => {
  if (!v || !v.image) return false;
  const n = String(v.name || "").trim();
  if (!n) return false;
  if (/^TEST[_\s]/i.test(n)) return false;
  return true;
};

export default function FleetVariety() {
  const [vehicles, setVehicles] = useState(null);
  const [imagesReady, setImagesReady] = useState(false);
  const [pickingId, setPickingId] = useState(null);
  const navigate = useNavigate();

  const scrollerRef = useRef(null);
  const trackRef = useRef(null);
  // Live playback state driven by rAF. Mutated without React re-renders.
  //   offset  — current translateX in pixels (positive = leftward shift)
  //   half    — pixel width of one card-set copy (A + A' → we translate 0..half)
  //   speed   — auto-play px/s (constant)
  //   dragging, dragStartX, dragStartOffset — pointer drag session state
  //   idleAt  — timestamp after which auto-play may resume post-drag
  //   inView  — IntersectionObserver flag
  const state = useRef({
    offset: 0,
    half: 0,
    speed: 60, // px/s — comfortable, per approved brief
    dragging: false,
    dragStartX: 0,
    dragStartOffset: 0,
    lastMoveX: 0,
    lastMoveTs: 0,
    velocity: 0, // px/s carried after release (inertia)
    idleAt: 0,
    inView: false,
    manuallyPaused: false,
    rafId: 0,
    lastTs: 0,
  });

  // ---- Data load + preload -------------------------------------------------
  useEffect(() => {
    reservationApi
      .vehicles()
      .then((list) => setVehicles(mix((list || []).filter(isRenderable))))
      .catch((err) => {
        console.error("[FleetVariety] failed to load /api/vehicles", err);
        setVehicles([]);
      });
  }, []);

  useEffect(() => {
    if (!vehicles || vehicles.length === 0) return undefined;
    let cancelled = false;
    Promise.all(
      vehicles.slice(0, Math.min(3, vehicles.length)).map(
        (v) =>
          new Promise((res) => {
            const im = new Image();
            im.onload = im.onerror = () => res();
            im.src = v.image;
          })
      )
    ).then(() => {
      if (!cancelled) setImagesReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [vehicles]);

  // ---- rAF loop: applies offset, wraps circularly, auto-plays when idle ---
  const applyTransform = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const s = state.current;
    // Wrap into [0, half). Because the DOM renders the vehicle list twice
    // (A + A') back-to-back, translating between these two anchor points is
    // visually indistinguishable → seamless infinite loop in both directions.
    if (s.half > 0) {
      while (s.offset >= s.half) s.offset -= s.half;
      while (s.offset < 0) s.offset += s.half;
    }
    track.style.transform = `translate3d(${-s.offset}px, 0, 0)`;
  }, []);

  useEffect(() => {
    if (!vehicles || vehicles.length === 0 || !imagesReady) return undefined;
    const track = trackRef.current;
    const scroller = scrollerRef.current;
    if (!track || !scroller) return undefined;
    // Capture the ref value now so the cleanup below operates on the same
    // state object even if the ref is reassigned before cleanup runs.
    const capturedState = state.current;

    // Measure the width of ONE list copy. trackRef contains two copies laid
    // out flexbox-nowrap, so half = scrollWidth / 2.
    const measure = () => {
      const half = track.scrollWidth / 2;
      if (half > 0) state.current.half = half;
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(track);
    ro.observe(scroller);
    window.addEventListener("resize", measure);

    // rAF scheduler: runs only while the carousel is actually visible.
    // Preloading, auto-motion, manual pause, drag, momentum and looping
    // remain unchanged; only off-screen/background work is suspended.
    let frameScheduled = false;

    const tick = (ts) => {
      const s = state.current;
      frameScheduled = false;
      s.rafId = 0;

      if (!s.inView || document.hidden) {
        s.lastTs = 0;
        return;
      }

      if (!s.lastTs) s.lastTs = ts;
      const dt = ts - s.lastTs;
      s.lastTs = ts;

      if (!s.dragging && Math.abs(s.velocity) > 0.5) {
        s.offset += (s.velocity * dt) / 1000;
        const decay = Math.exp(-dt / 320);
        s.velocity *= decay;
        if (Math.abs(s.velocity) < 4) s.velocity = 0;
      }

      const now = performance.now();
      const canAuto =
        !s.manuallyPaused &&
        !s.dragging &&
        Math.abs(s.velocity) < 1 &&
        now >= s.idleAt &&
        s.half > 0;
      if (canAuto && !prefersReducedMotion()) {
        s.offset += (s.speed * dt) / 1000;
      }
      applyTransform();
      scheduleFrame();
    };

    const scheduleFrame = () => {
      const s = state.current;
      if (frameScheduled || !s.inView || document.hidden) return;
      frameScheduled = true;
      s.rafId = requestAnimationFrame(tick);
    };

    const stopFrame = () => {
      const s = state.current;
      if (s.rafId) cancelAnimationFrame(s.rafId);
      s.rafId = 0;
      s.lastTs = 0;
      frameScheduled = false;
    };

    const io = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (!e) return;
        const s = state.current;
        const wasInView = s.inView;
        s.inView = e.isIntersecting;

        if (e.isIntersecting && !wasInView) {
          s.manuallyPaused = false;
          s.velocity = 0;
          s.lastTs = performance.now();
          scheduleFrame();
        } else if (!e.isIntersecting) {
          stopFrame();
        }
      },
      { threshold: 0.15 }
    );
    io.observe(scroller);

    const onVisibilityChange = () => {
      if (document.hidden) stopFrame();
      else scheduleFrame();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      stopFrame();
      capturedState.rafId = 0;
      capturedState.lastTs = 0;
      io.disconnect();
      ro.disconnect();
      window.removeEventListener("resize", measure);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [vehicles, imagesReady, applyTransform]);

  // ---- Pointer drag: bidirectional 1:1 manual pan with inertial release ---
  const onPointerDown = (e) => {
    if (e.button && e.button !== 0) return;
    const s = state.current;

    // Any deliberate interaction stops ambient movement for this viewport
    // visit. Interactive controls must retain their native click behavior;
    // the carousel must never capture their pointer.
    s.manuallyPaused = true;
    if (e.target.closest?.("button, a, input, select, textarea, [role='button']")) {
      s.velocity = 0;
      return;
    }

    s.dragging = true;
    s.dragStartX = e.clientX;
    s.dragStartOffset = s.offset;
    s.lastMoveX = e.clientX;
    s.lastMoveTs = performance.now();
    s.velocity = 0; // cancel any residual inertia
    s.idleAt = performance.now() + 800;
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch {}
  };
  const onPointerMove = (e) => {
    const s = state.current;
    if (!s.dragging) return;
    const dx = e.clientX - s.dragStartX;
    // 1:1 direct pan — do NOT scale, brake, or clamp. Auto motion is
    // paused during drag so the two never fight.
    s.offset = s.dragStartOffset - dx;
    // Track short-window velocity for the release flick. Sampling last-move
    // delta / dt gives natural inertia proportional to swipe speed.
    const now = performance.now();
    const mdt = now - s.lastMoveTs;
    if (mdt > 0) {
      // Instantaneous px/s: negative dx (finger moving left) → offset
      // increasing → post-release velocity should be positive to continue
      // moving carousel left. offset_delta = -(e.clientX - lastMoveX).
      const inst = (-(e.clientX - s.lastMoveX) / mdt) * 1000;
      // Low-pass filter (weighted blend) to smooth micro jitter.
      s.velocity = s.velocity * 0.25 + inst * 0.75;
    }
    s.lastMoveX = e.clientX;
    s.lastMoveTs = now;
    applyTransform();
  };
  const endDrag = (e) => {
    const s = state.current;
    if (!s.dragging) return;
    s.dragging = false;
    // If the last velocity sample is stale (>80ms since last move), the user
    // paused before lifting — no inertia. Otherwise, let the coast run.
    const idle = performance.now() - s.lastMoveTs > 80;
    if (idle) s.velocity = 0;
    // Cap inertia so a wild flick doesn't spin for seconds.
    if (s.velocity > 3000) s.velocity = 3000;
    if (s.velocity < -3000) s.velocity = -3000;
    // Give inertia room to run; auto motion won't take over until both the
    // idle window elapses AND velocity has decayed below ~1 px/s.
    s.idleAt = performance.now() + 800;
    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    } catch {}
  };

  // Wheel/trackpad — bidirectional but ONLY on explicit horizontal wheel
  // (deltaX). Vertical wheel is left entirely alone so vertical page scroll
  // (and Lenis smooth scroll) always passes through cleanly.
  const onWheel = (e) => {
    const s = state.current;
    // Only meaningful horizontal wheel intent — trackpad two-finger swipe
    // or shift-wheel on a mouse. Never react to plain vertical wheel.
    if (Math.abs(e.deltaX) < 4 || Math.abs(e.deltaX) < Math.abs(e.deltaY)) return;
    e.preventDefault();
    s.manuallyPaused = true;
    s.offset += e.deltaX;
    s.idleAt = performance.now() + 1500;
    applyTransform();
  };

  const onKeyDown = (e) => {
    const s = state.current;
    if (e.key === "ArrowRight") {
      s.manuallyPaused = true;
      s.offset += 220;
      s.idleAt = performance.now() + 1500;
      applyTransform();
    } else if (e.key === "ArrowLeft") {
      s.manuallyPaused = true;
      s.offset -= 220;
      s.idleAt = performance.now() + 1500;
      applyTransform();
    }
  };

  const pick = async (v) => {
    if (
      pickingId ||
      !v ||
      v.coming_soon === true ||
      v.bookable === false ||
      String(v.id || "").startsWith("elite-")
    ) return;

    state.current.manuallyPaused = false;
    state.current.velocity = 0;
    setPickingId(v.id);
    try {
      // Fresh booking-intent semantics (2026-07-28): direct Fleet Variety
      // vehicle entry must not inherit a stale service_type / trip_type
      // from a previous abandoned service-card journey. The customer
      // gets to pick Hourly vs Point-to-Point themselves on Trip Details.
      // localStorage's active service intent is also cleared.
      try { localStorage.removeItem("voor_service_type"); } catch {}
      const staleReset = [
        "service_type", "trip_type", "hourly_duration", "ending_mode", "stops",
        "pickup_location", "dropoff_location", "pickup_date", "pickup_time",
      ];
      await reservationApi.upsertDraft({
        current_step: "trip",
        reset_fields: staleReset,
        service_type: "",
        selected_vehicle: {
          id: v.id,
          name: v.name,
          category: v.category,
          category_key: v.category_key,
          klass: v.klass,
          subtitle: v.subtitle,
          make: v.make,
          model: v.model,
          color: v.color,
          passengers: v.passengers,
          luggage: v.luggage,
          price_placeholder: v.price_placeholder,
          image: v.image,
          features: v.features || [],
        },
      });
      navigate("/reserve");
    } catch (e) {
      setPickingId(null);
      toast.error("Could not select vehicle", {
        description: e?.response?.data?.detail || "Please try again.",
      });
    }
  };

  return (
    <section id="fleet" data-testid="fleet-variety-section" className="px-4 pt-6 sm:pt-10">
      {/* OR bridge — large bright yellow, clearly signals the second entry. */}
      <div className="mx-auto max-w-6xl text-center mb-6 sm:mb-8" data-testid="fleet-or-bridge">
        <div
          className="font-display font-bold text-[#d6d400] leading-none"
          style={{
            fontSize: "clamp(56px, 8vw, 96px)",
            letterSpacing: "0.02em",
            textShadow: "0 0 26px rgba(214,212,0,0.35)",
          }}
          aria-hidden="true"
        >
          OR
        </div>
        <h3
          className="font-display font-semibold text-[#f4f4ea] mt-3 sm:mt-4"
          data-testid="fleet-picker-heading"
          style={{ fontSize: "clamp(20px, 3vw, 34px)", letterSpacing: "-0.005em", lineHeight: 1.2 }}
        >
          Start By Picking Your Favorite Vehicle
        </h3>
        <p
          className="font-display uppercase tracking-[0.24em] text-[#d6d400] mt-3"
          style={{ fontSize: "clamp(11px, 1vw, 13px)" }}
        >
          Explore the Fleet
        </p>
      </div>

      <div className="mx-auto max-w-6xl vb-panel p-4 sm:p-6 lg:p-8">
        <SectionHeading testId="fleet-variety-heading">Fleet Variety</SectionHeading>
        <p className="text-center font-body text-[#8f9080] text-[12px] sm:text-sm mt-3 sm:mt-4">
          Every vehicle in the fleet — a curated luxury gallery.
        </p>

        {vehicles === null && (
          <div className="flex justify-center py-14" data-testid="fleet-variety-loading">
            <Loader2 className="animate-spin text-[#d6d400]" size={26} />
          </div>
        )}

        {vehicles && vehicles.length === 0 && (
          <p
            data-testid="fleet-variety-empty"
            className="text-center font-body text-[#8f9080] text-sm py-10"
          >
            The fleet is currently being updated. Check back soon.
          </p>
        )}

        {vehicles && vehicles.length > 0 && (
          <div
            ref={scrollerRef}
            data-testid="fleet-variety-scroller"
            role="region"
            aria-label="Fleet gallery. Drag or use arrow keys to browse; loops infinitely in both directions."
            tabIndex={0}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onPointerLeave={endDrag}
            onLostPointerCapture={endDrag}
            onWheel={onWheel}
            onKeyDown={onKeyDown}
            className="mt-6 sm:mt-8 fv-scroller select-none"
            style={{
              overflow: "hidden",
              cursor: "grab",
              opacity: imagesReady ? 1 : 0,
              transition: "opacity 260ms ease-out",
              minHeight: "260px",
              touchAction: "pan-y", // let vertical page scroll pass through
            }}
          >
            <style>{`
              .fv-scroller:active { cursor: grabbing; }
              .fv-track { will-change: transform; }
            `}</style>
            <div
              ref={trackRef}
              className="fv-track flex flex-nowrap gap-3 sm:gap-4"
              style={{ minWidth: "min-content" }}
            >
              {[false, true].flatMap((clone) =>
                vehicles.map((v) => {
                  const k = clone ? `clone-${v.id}` : v.id;
                  const tid = (name) => (clone ? undefined : name);
                  return (
                    <div
                      key={k}
                      aria-hidden={clone ? "true" : undefined}
                      data-testid={tid(`fleet-variety-card-${v.id}`)}
                      className="vb-card shrink-0 w-[240px] sm:w-[280px] lg:w-[300px] flex flex-col p-3 sm:p-4"
                    >
                      <div
                        className="relative overflow-hidden rounded-[10px]"
                        style={{ aspectRatio: "16 / 10", background: "#050505" }}
                      >
                        <img
                          src={v.image}
                          alt={v.name}
                          width={600}
                          height={375}
                          className="absolute inset-0 w-full h-full object-cover"
                          loading="lazy"
                          decoding="async"
                          draggable={false}
                          onError={(e) => {
                            // Never leave a blank/beige panel. Swap to the
                            // safe VOOR silhouette placeholder if the source
                            // ever fails at runtime.
                            e.currentTarget.src = "/vehicles/placeholder.svg";
                          }}
                        />
                        {v.klass ? (
                          <span
                            className="absolute top-2 left-2 font-display font-bold uppercase tracking-[0.14em] text-[9px] sm:text-[10px] text-[#050505] px-2.5 py-1 rounded-full"
                            style={{
                              background: "linear-gradient(180deg, #d6d400 0%, #b0af00 100%)",
                              boxShadow: "0 2px 8px rgba(214,212,0,0.35)",
                            }}
                            data-testid={tid(`fleet-variety-badge-${v.id}`)}
                          >
                            {v.klass}
                          </span>
                        ) : (
                          <span
                            className="absolute top-2 left-2 font-display uppercase tracking-[0.12em] text-[9px] sm:text-[10px] text-[#d6d400] px-2 py-1 rounded-full"
                            style={{
                              background: "rgba(5,5,5,0.72)",
                              border: "1px solid rgba(214,212,0,0.4)",
                            }}
                            data-testid={tid(`fleet-variety-badge-${v.id}`)}
                          >
                            {CAT_BADGE[v.category_key] || v.category || "Fleet"}
                          </span>
                        )}
                        {/* Public BLACK/WHITE color badge removed 2026-07-28:
                            the color is visually obvious from the vehicle
                            photo. Stored `v.color` remains untouched. */}
                      </div>

                      <div className="mt-3 flex-1">
                        <div className="font-display font-bold text-[#f4f4ea] tracking-wide text-sm sm:text-base leading-tight">
                          {v.name}
                        </div>
                        {v.subtitle && (
                          <div className="font-body text-[12px] sm:text-xs text-[#8f9080] mt-1 leading-snug line-clamp-2">
                            {v.subtitle}
                          </div>
                        )}
                        <div className="flex items-center gap-3 mt-2 font-body text-[#c9c9ba] text-[12px] sm:text-xs">
                          <span className="inline-flex items-center gap-1">
                            <Users size={12} className="text-[#d6d400]" /> {v.passengers}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <Briefcase size={12} className="text-[#d6d400]" /> {v.luggage}
                          </span>
                          {/* Preliminary `$<price_placeholder>` intentionally
                              hidden per owner directive (2026-07-27). Vehicle
                              data (price_placeholder field) remains untouched. */}
                        </div>
                      </div>

                      <button
                        type="button"
                        data-testid={tid(`pick-to-book-${v.id}`)}
                        onClick={() => { if (v.coming_soon !== true && v.bookable !== false && !String(v.id || "").startsWith("elite-")) pick(v); }}
                        disabled={pickingId !== null || v.coming_soon === true || v.bookable === false || String(v.id || "").startsWith("elite-")}
                        aria-disabled={v.coming_soon === true || v.bookable === false || String(v.id || "").startsWith("elite-")}
                        tabIndex={clone ? -1 : 0}
                        aria-hidden={clone ? "true" : undefined}
                        className="vb-btn vb-btn-solid w-full inline-flex items-center justify-center gap-2 mt-3 py-2.5 text-[11px] sm:text-xs disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {pickingId === v.id ? (
                          <>
                            <Loader2 size={13} className="animate-spin" /> Loading
                          </>
                        ) : (v.coming_soon === true || v.bookable === false || String(v.id || "").startsWith("elite-")) ? (
                          <>Coming Soon</>
                        ) : (
                          <>
                            Pick To Book <ArrowRight size={13} />
                          </>
                        )}
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
        {/* View the Full Fleet — dedicated fleet category page route. */}
        <div className="text-center mt-8 sm:mt-10">
          <Link
            to="/fleet/sedan"
            data-testid="fleet-view-all"
            className="vb-btn inline-flex items-center gap-2 px-6 h-11 font-display uppercase tracking-[0.14em] text-xs text-[#d6d400] border-[1.5px] border-[#d6d400] hover:bg-[#d6d40010] transition-colors"
          >
            View the Full Fleet
          </Link>
        </div>
      </div>
    </section>
  );
}

function prefersReducedMotion() {
  try {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}
